import { Link } from "@tanstack/react-router";
import { CheckCircle, Clock, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOwnerPayAppSubBreakdown } from "@/hooks/use-pay-applications";

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface Props {
  payAppId: string;
  costCodeId: string | null;
  costCodeDesc: string;
  thisPeriodTotal: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SubSourcedDrillDownSheet({
  payAppId,
  costCodeId,
  costCodeDesc,
  thisPeriodTotal,
  open,
  onOpenChange,
}: Props) {
  const { data: breakdown, isLoading } = useOwnerPayAppSubBreakdown(payAppId);

  const entry = (breakdown ?? []).find(
    (e) => e.ownerCostCodeId === costCodeId || e.ownerDescription === costCodeDesc,
  );
  const contributions = entry?.contributions ?? [];

  const contributionTotal = contributions.reduce((sum, c) => sum + c.totalAmount, 0);
  const checksumMatch = Math.abs(contributionTotal - thisPeriodTotal) < 0.01;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-2">
          <DialogTitle>{costCodeDesc}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            This Period: <span className="font-medium">{formatCurrency(thisPeriodTotal)}</span>
          </p>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : contributions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No subcontractor contributions found for this line item.
          </p>
        ) : (
          <>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Subcontractor / Pay App</TableHead>
                  <TableHead>Line Item</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Approval</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contributions
                  .slice()
                  .sort(
                    (a, b) =>
                      b.totalAmount - a.totalAmount ||
                      a.subCompanyName.localeCompare(b.subCompanyName),
                  )
                  .map((c) => (
                    <TableRow key={`${c.subPayAppId}-${c.subLineDescription}`}>
                      <TableCell>
                        <div className="space-y-0.5">
                          <span className="font-medium block">{c.subCompanyName}</span>
                          {c.commitmentTitle ? (
                            <span className="block text-[10px] text-muted-foreground">
                              {c.commitmentTitle}
                            </span>
                          ) : null}
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] font-mono text-muted-foreground">
                              Pay App #{c.subPayAppNo}
                              {c.commitmentNumber ? ` · ${c.commitmentNumber}` : ""}
                            </span>
                            <Button variant="ghost" size="icon" className="h-4 w-4" asChild>
                              <Link
                                to="/pay-applications/subcontract/$id"
                                params={{ id: c.subPayAppId }}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.costCodeCode ? (
                          <span className="block font-mono text-[10px] text-muted-foreground">
                            {c.costCodeCode}
                          </span>
                        ) : null}
                        <span className="block text-[11px]">{c.subLineDescription || "--"}</span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {c.billingPeriodEnd ? formatDate(c.billingPeriodEnd) : "--"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(c.totalAmount)}
                        {c.storedMaterials > 0 ? (
                          <span className="block text-[10px] text-muted-foreground">
                            +{formatCurrency(c.storedMaterials)} stored
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {c.approvalStatus === "APPROVED" || c.approvalStatus === "approved" ? (
                          <div>
                            <Badge variant="success" className="text-[10px]">
                              <CheckCircle className="mr-1 h-2.5 w-2.5" />
                              Approved
                            </Badge>
                            {c.approverName ? (
                              <span className="block text-[10px] text-muted-foreground mt-0.5">
                                {c.approverName}
                                {c.approvedAt ? ` · ${formatDate(c.approvedAt)}` : ""}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <Badge variant="warning" className="text-[10px]">
                            <Clock className="mr-1 h-2.5 w-2.5" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            {/* Checksum footer */}
            <div
              className={`mt-4 rounded-md border px-3 py-2 text-xs ${checksumMatch ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300" : "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"}`}
            >
              Total from {contributions.length} sub{contributions.length !== 1 ? "s" : ""}:{" "}
              <span className="font-medium">{formatCurrency(contributionTotal)}</span>
              {!checksumMatch ? (
                <span className="ml-2">
                  (differs from line total {formatCurrency(thisPeriodTotal)})
                </span>
              ) : null}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
